"use client";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@repo/ui/components/dialog";
import { Download, X } from "lucide-react";

/**
 * Where the bytes come from when somebody asks to save one.
 *
 * Our own origin, always. Both hosts that hold these files answer without CORS
 * headers, and a browser drops the `download` attribute on a cross-origin link
 * -- so a direct link would navigate to the picture and lose the thread behind
 * it. The route on the other end is what turns the fetch into a save.
 */
export function attachmentDownloadHref(url: string): string {
  return `/api/expert-agent/attachment/download?url=${encodeURIComponent(url)}`;
}

const CHROME =
  "flex size-9 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm transition-colors hover:bg-black/75 focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:outline-hidden";

/**
 * A picture in a thread, at the size the thread can spare and at the size it
 * was sent.
 *
 * The thumbnail is capped at 15rem because a conversation is what the panel is
 * for, which is fine right up until the attachment is the thing being discussed:
 * a screenshot of somebody's dashboard is unreadable at that width, and for as
 * long as the picture was an `<img>` and nothing else there was nowhere else to
 * read it. Pressing it is the way out, and the file itself is one click further.
 */
export function AttachmentImage({ url }: { url: string }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        {/* The button carries the name; the image inside it is decorative, or a
            screen reader announces the same attachment twice. */}
        <button
          type="button"
          aria-label="Open attachment"
          className="imsg-attachment-button"
        >
          {/* eslint-disable-next-line @next/next/no-img-element -- a CloudFront
              url the optimizer has no remotePattern for */}
          <img src={url} alt="" className="imsg-attachment" />
        </button>
      </DialogTrigger>

      <DialogContent
        hideCloseButton
        // No card around a photo: the frame is the picture, the way the bubble
        // already gets out of its way in the thread. The width cap is the
        // viewport rather than `max-w-lg`, since being able to read the thing is
        // the entire point of opening it.
        className="w-auto max-w-[min(95vw,72rem)] border-0 bg-transparent p-0 shadow-none"
        overlayClassName="bg-black/80"
        // Nothing to describe: the dialog is one picture.
        aria-describedby={undefined}
      >
        <DialogTitle className="sr-only">Attachment</DialogTitle>

        <div className="absolute top-3 right-3 z-10 flex gap-2">
          <a
            href={attachmentDownloadHref(url)}
            download
            aria-label="Download attachment"
            className={CHROME}
          >
            <Download className="size-4" aria-hidden />
          </a>
          <DialogClose aria-label="Close" className={CHROME}>
            <X className="size-4" aria-hidden />
          </DialogClose>
        </div>

        {/* eslint-disable-next-line @next/next/no-img-element -- same url, same
            reason as the thumbnail above */}
        <img
          src={url}
          alt="Attachment"
          className="mx-auto max-h-[90dvh] w-auto rounded-lg object-contain"
        />
      </DialogContent>
    </Dialog>
  );
}
